import { Capacitor } from '@capacitor/core'
import { StatusBar, Style } from '@capacitor/status-bar'

/**
 * Plugin to initialize StatusBar configuration on app startup
 * This ensures the StatusBar is properly configured for all pages
 */
export default defineNuxtPlugin({
  name: 'status-bar-initializer',
  enforce: 'pre', // Run before other plugins
  async setup() {
    // Only run in client-side code on native platforms
    if (!process.client || !Capacitor.isNativePlatform()) return

    try {
      console.info('[StatusBar] Initializing StatusBar configuration on app startup')
      
      // Check if we're on Android - StatusBar plugin is not implemented on Android
      const isAndroidPlatform = Capacitor.getPlatform() === 'android'
      
      // Skip StatusBar operations on Android
      if (isAndroidPlatform) {
        console.info('[StatusBar] Android platform detected - StatusBar plugin not implemented, skipping configuration')
        // We'll use SystemUI plugin via android-immersive for the Clock page instead
        return
      }
      
      // Only continue for iOS and other platforms where StatusBar is implemented
      
      // Check if we're on the clock page at startup
      const isClockPage = window.location.pathname.includes('/user/clock')
      console.info(`[StatusBar] Current path: ${window.location.pathname}, isClockPage: ${isClockPage}`)
      
      // PRIORITY handling for Clock page
      if (isClockPage) {
        console.info('[StatusBar] PRIORITY: Clock page detected, configuring for immersive mode')
        
        // For Clock page, we want StatusBar to be in overlay mode for immersive experience
        try {
          // Make status bar transparent and overlaying content
          await StatusBar.setOverlaysWebView({ overlay: true })
          console.info('[StatusBar] Set overlay=true for Clock page')
          
          // Set style to dark for consistent appearance
          await StatusBar.setStyle({ style: Style.Dark })
          
          // Set background to black (will be visible during transitions)
          await StatusBar.setBackgroundColor({ color: '#000000' })
          
          // On Clock page, the StatusBar will be hidden by immersive mode,
          // but we still show it first to ensure proper transitions
          await StatusBar.show()
          console.info('[StatusBar] StatusBar configured for Clock page immersive mode')
        } catch (err) {
          console.error('[StatusBar] Error configuring StatusBar for Clock page:', err)
        }
      } else {
        // For all other pages, disable overlay
        console.info('[StatusBar] Standard page detected, configuring for normal mode')
        try {
          // Ensure StatusBar is not overlaying content (standard behavior)
          await StatusBar.setOverlaysWebView({ overlay: false })
          console.info('[StatusBar] Set overlay=false for standard page')
          
          // Set style to dark for consistent appearance
          await StatusBar.setStyle({ style: Style.Dark })
          
          // Set background to black to match app theme
          await StatusBar.setBackgroundColor({ color: '#000000' })
          
          // Make sure StatusBar is visible
          await StatusBar.show()
          console.info('[StatusBar] StatusBar configured for standard page')
        } catch (err) {
          console.error('[StatusBar] Error configuring StatusBar for standard page:', err)
        }
      }
      
      console.info('[StatusBar] StatusBar initialization complete')
    } catch (err) {
      console.error('[StatusBar] Fatal error in StatusBar initialization:', err)
    }
  }
}) 